import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  orangeMui,
  rydeColors,
} from "@ryde/theme";

import {
  ANOMALY_LIST_MAX_HEIGHT_PX,
  PASSENGER_ROW_MIN_HEIGHT_PX,
} from "./investigation.constants";

export const PanelBodyStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel",
})`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 2px 24px;
`;

export const PanelTabsBarStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-tabs",
})`
  flex: 0 0 auto;
  background: ${rydeColors.white};
  border-bottom: 1px solid ${greyMui._200};
`;

/**
 * Host for a tab that must survive being switched away from — hidden, not unmounted. The Ask-AI tab
 * owns a conversation: re-mounting it on every glance at the station list would throw the transcript
 * (and the advisor session it belongs to) away.
 */
export const PanelTabHostStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-tab-host",
})<{ $active: boolean }>`
  display: ${({ $active }) => ($active ? "flex" : "none")};
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

export const PanelSectionStyled = styled.section.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-section",
})`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PanelSectionTitleStyled = styled.h4.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-section-title",
})`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${greyMui._600};
`;

export const StatGridStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-stat-grid",
})`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

export const StatTileStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-stat-tile",
})`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border: 1px solid ${greyMui._200};
  border-radius: 8px;
  background: ${greyMui._50};

  .tile-label {
    font-size: 11px;
    color: ${greyMui._600};
  }

  .tile-value {
    font-size: 16px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${blueGreyMui._800};
  }
`;

export const DefinitionRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-definition-row",
})`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px dashed ${greyMui._200};
  font-size: 13px;

  .def-label {
    color: ${greyMui._600};
    flex: 0 0 auto;
  }

  .def-value {
    color: ${blueGreyMui._900};
    font-weight: 600;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

export const StationRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-station-row",
})<{ $active: boolean }>`
  display: flex;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? blueMui._300 : "transparent")};
  background: ${({ $active }) => ($active ? blueMui._50 : "transparent")};
  cursor: pointer;

  &:hover {
    border-color: ${blueMui._300};
  }

  .station-index {
    flex: 0 0 22px;
    height: 22px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 700;
    color: ${rydeColors.white};
    background: ${({ $active }) => ($active ? blueMui._600 : greyMui._400)};
  }

  .station-body {
    flex: 1 1 auto;
    min-width: 0;
  }

  .station-name {
    font-size: 13.5px;
    font-weight: 600;
    color: ${blueGreyMui._900};
  }

  .station-times {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 3px;
    font-size: 12px;
    color: ${greyMui._700};
  }

  .station-times .muted {
    color: ${greyMui._500};
  }

  .station-flow {
    display: flex;
    gap: 12px;
    margin-top: 4px;
    font-size: 12px;
    color: ${blueGreyMui._700};
  }
`;

/**
 * One passenger in the roster. A real `<button>`, not a clickable row: the whole thing seeks playback
 * to that passenger's boarding, so it has to be reachable and operable from the keyboard.
 */
export const PassengerRowStyled = styled.button.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-passenger-row",
})<{ $active: boolean }>`
  display: flex;
  gap: 10px;
  width: 100%;
  min-height: ${PASSENGER_ROW_MIN_HEIGHT_PX}px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? blueMui._300 : "transparent")};
  background: ${({ $active }) => ($active ? blueMui._50 : "transparent")};
  font: inherit;
  color: inherit;
  text-align: start;
  cursor: pointer;

  &:hover {
    border-color: ${blueMui._300};
  }

  &:focus-visible {
    outline: 2px solid ${blueMui._600};
    outline-offset: 2px;
  }

  .pax-seq {
    flex: 0 0 22px;
    height: 22px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 700;
    color: ${rydeColors.white};
    background: ${({ $active }) => ($active ? blueMui._600 : greyMui._400)};
  }

  .pax-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .pax-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .pax-name {
    font-size: 13.5px;
    font-weight: 600;
    color: ${blueGreyMui._900};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pax-legs {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
    font-size: 12px;
    color: ${greyMui._700};
  }

  .pax-legs .muted {
    color: ${greyMui._500};
  }

  .pax-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
    font-size: 12px;
    color: ${blueGreyMui._700};
  }

  .pax-note {
    font-size: 12px;
    font-style: italic;
    color: ${greyMui._600};
    overflow-wrap: anywhere;
  }
`;

export const PassengerStatusChipStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-passenger-status",
})<{ $color: string; $bg: string }>`
  flex: 0 0 auto;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`;

export const DelayChipStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-delay-chip",
})<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${greyMui._100};
`;

export const EventRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-event-row",
})<{ $color: string; $bg: string; $active: boolean }>`
  display: flex;
  gap: 10px;
  padding: 6px 4px;
  border-bottom: 1px solid ${greyMui._100};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? blueMui._50 : "transparent")};
  box-shadow: ${({ $active }) =>
    $active ? `inset 0 0 0 1.5px ${blueMui._300}` : "none"};
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? blueMui._50 : greyMui._50)};
  }

  .event-icon {
    flex: 0 0 26px;
    height: 26px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
  }

  .event-icon svg {
    font-size: 16px;
  }

  .event-body {
    flex: 1 1 auto;
    min-width: 0;
  }

  .event-label {
    font-size: 13px;
    font-weight: 600;
    color: ${blueGreyMui._900};
  }

  .event-meta {
    font-size: 12px;
    color: ${greyMui._600};
    margin-top: 1px;
  }

  .event-time {
    flex: 0 0 auto;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${greyMui._500};
  }
`;

export const AnomalyBannerStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-anomaly-banner",
})`
  flex: 0 0 auto;
  margin: 12px 14px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${orangeMui._200};
  background: ${orangeMui._50};
`;

/**
 * Scrolls the anomaly rows instead of letting them grow the banner. A ride with dozens of late arrivals was
 * pushing the tabs — and the tab content with them — off the bottom of the panel. The "ANOMALIES" heading
 * stays outside this box so it remains visible while the list scrolls.
 */
export const AnomalyListStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-anomaly-list",
})`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  max-height: ${ANOMALY_LIST_MAX_HEIGHT_PX}px;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const AnomalyRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-anomaly-row",
})<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: ${blueGreyMui._900};

  .anomaly-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`;

export const SparkWrapStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-spark",
})`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .spark-caption {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: ${greyMui._600};
  }

  svg {
    width: 100%;
    height: 56px;
    display: block;
  }
`;

export const PanelMessageStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-message",
})`
  padding: 24px 12px;
  text-align: center;
  font-size: 13px;
  color: ${greyMui._600};
`;

/** Centers the colored captioned loader while the investigation payload/trail load. */
export const PanelLoaderStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-panel-loader",
})`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  padding: 32px 12px;
`;

/** One GPS sample row in the Tracing tab (time · speed · optional detour badge); click seeks. */
export const TracingRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-tracing-row",
})<{ $detour: boolean; $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 6px;
  border-bottom: 1px solid ${greyMui._100};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? blueMui._50 : "transparent")};
  box-shadow: ${({ $active }) =>
    $active ? `inset 0 0 0 1.5px ${blueMui._300}` : "none"};
  font-size: 12.5px;
  cursor: pointer;
  color: ${blueGreyMui._800};

  &:hover {
    background: ${({ $active }) => ($active ? blueMui._50 : greyMui._50)};
  }

  .tracing-time {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
    color: ${blueGreyMui._900};
  }

  .tracing-speed {
    font-variant-numeric: tabular-nums;
    color: ${greyMui._700};
  }

  .tracing-detour {
    margin-left: auto;
    font-weight: 600;
    color: ${({ $detour }) => ($detour ? orangeMui._700 : "transparent")};
  }
`;
